import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Tooltip,
    Grid,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Button,
    Stack,
    Chip
} from '@mui/material';
import {
    ArrowBackIos,
    ArrowForwardIos,
    Assignment,
    EventAvailable,
    FitnessCenter,
    ZoomIn,
    ZoomOut
} from '@mui/icons-material';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import 'dayjs/locale/pt';
dayjs.extend(minMax);
dayjs.locale('pt');
import { motion } from 'framer-motion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import { useRecords } from '../../../../hooks/useRecords';
import EventDetailsCard from './EventDetailsCard';

const getEventColor = (type) => {
    switch (type) {
        case 'exercise':
            return '#1976d2';
        case 'questionnaire':
            return '#2e7d32';
        case 'consult':
            return '#9c27b0';
        default:
            return '#757575';
    }
};

const getEventIcon = (type) => {
    switch (type) {
        case 'exercise':
            return <FitnessCenter sx={{ color: '#fff', fontSize: 16 }} />;
        case 'questionnaire':
            return <Assignment sx={{ color: '#fff', fontSize: 16 }} />;
        case 'consult':
            return <EventAvailable sx={{ color: '#fff', fontSize: 16 }} />;
        default:
            return null;
    }
};

export default function PatientPlanTimeline({
    patient,
    planUuid,
    startData,
    endData,
    selected,
    expanded,
    selectedPlan
}) {
    const { data, isLoading } = useRecords(patient, null, planUuid, true);

    const today = dayjs();

    const start = useMemo(() => {
        if (startData) return dayjs(startData);
        if (data?.length) return dayjs.min(data.map((r) => dayjs(r.ti)));
        return dayjs().subtract(1, 'month');
    }, [startData, data]);

    const end = useMemo(() => {
        if (endData) return dayjs(endData);
        if (data?.length) return dayjs.max(data.map((r) => dayjs(r.tf)));
        return dayjs();
    }, [endData, data]);

    const safeEnd = end.isBefore(start) ? start.add(1, 'day') : end;

    const totalDays = safeEnd.diff(start, 'day');

    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [zoom, setZoom] = useState(1);
    const baseDayWidth = 20;
    const [visibleTypes, setVisibleTypes] = useState({
        exercise: true,
        questionnaire: false,
        consult: false
    });
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);

    useEffect(() => {
        if (!containerRef.current) return;
        const handleResize = () => setContainerWidth(containerRef.current.offsetWidth);
        handleResize();

        const observer = new ResizeObserver(handleResize);
        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        if (!containerRef.current || !data?.length) return;

        const updateZoom = () => {
            if (!containerRef.current) return;

            const width = containerRef.current?.offsetWidth ?? 0;
            if (!width || totalDays <= 0) return;

            const neededWidth = totalDays * baseDayWidth;
            const endMarkerOffset = 30;
            const idealZoom = (width - endMarkerOffset * 2) / neededWidth;

            setZoom((prev) => (Math.abs(prev - idealZoom) > 0.01 ? idealZoom : prev));
        };

        updateZoom();

        const observer = new ResizeObserver(() => {
            if (!containerRef.current) return;
            requestAnimationFrame(updateZoom);
        });

        if (containerRef.current) observer.observe(containerRef.current);

        const timeout = setTimeout(() => {
            if (containerRef.current) updateZoom();
        }, 200);

        return () => {
            clearTimeout(timeout);
            observer.disconnect();
        };
    }, [data, totalDays, baseDayWidth]);

    const timelineWidth = useMemo(
        () => totalDays * baseDayWidth * zoom,
        [totalDays, zoom]
    );

    const eventsByDay = useMemo(() => {
        if (!data?.length) return [];
        const grouped = {};
        data.forEach((r) => {
            const date = dayjs(r.ti).format('YYYY-MM-DD');
            if (!grouped[date]) grouped[date] = [];
            grouped[date].push(r);
        });
        return Object.keys(grouped).map((date) => {
            const diff = dayjs(date).diff(start, 'day');
            const position = (diff / totalDays) * timelineWidth + 15;
            return {
                date,
                type: grouped[date][0].type?.toLowerCase() || 'exercise',
                position,
                events: grouped[date]
            };
        });
    }, [data, start, totalDays, timelineWidth]);

    const monthMarkers = useMemo(() => {
        const markers = [];
        let current = start.startOf('month').add(1, 'month');
        while (current.isBefore(safeEnd)) {
            const firstDayOfMonth = current.startOf('month');
            if (
                firstDayOfMonth.isAfter(start, 'day') &&
                firstDayOfMonth.isBefore(safeEnd, 'day')
            ) {
                const diff = firstDayOfMonth.diff(start, 'day');
                const position = (diff / totalDays) * timelineWidth + 15;
                markers.push({
                    month: firstDayOfMonth.format('MMM'),
                    position
                });
            }
            current = current.add(1, 'month');
        }
        return markers;
    }, [start, safeEnd, timelineWidth, totalDays]);

    const currentPosition = useMemo(() => {
        const todayClamped = today.isBefore(start)
            ? start
            : today.isAfter(safeEnd)
              ? safeEnd
              : today;
        const daysFromStart = todayClamped.diff(start, 'day');
        return (daysFromStart / totalDays) * timelineWidth;
    }, [timelineWidth, start, safeEnd, totalDays]);

    const progressWidth = currentPosition;

    const isDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const handleMouseDown = (e) => {
        isDown.current = true;
        startX.current = e.pageX - containerRef.current.offsetLeft;
        scrollLeft.current = containerRef.current.scrollLeft;
    };

    const handleMouseLeave = () => (isDown.current = false);
    const handleMouseUp = () => (isDown.current = false);
    const handleMouseMove = (e) => {
        if (!isDown.current) return;
        e.preventDefault();
        const x = e.pageX - containerRef.current.offsetLeft;
        const walk = (x - startX.current) * 1;
        containerRef.current.scrollLeft = scrollLeft.current - walk;
    };

    const handleToggleType = (type) => {
        setVisibleTypes((prev) => ({ ...prev, [type]: !prev[type] }));
    };

    if (isLoading) {
        return (
            <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography>{t('userPage.timeline.loading')}</Typography>
            </Card>
        );
    }

    return (
        <Card
            elevation={3}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: 2,
                borderRadius: 3,
                mb: 2,
                backgroundColor: '#F9F9F9'
            }}
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
        >
            <Grid container alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {t('userPage.timeline.title')}
                    </Typography>
                    <Box
                        sx={{
                            height: 3,
                            width: 125,
                            backgroundColor: '#1976d2',
                            borderRadius: 2
                        }}
                    />
                </Box>
                <Box>
                    <Tooltip title="Zoom In">
                        <IconButton onClick={() => setZoom((z) => Math.min(3, z + 0.25))}>
                            <ZoomIn />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Zoom Out">
                        <IconButton
                            onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                        >
                            <ZoomOut />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Grid>
            <Box
                sx={{
                    position: 'relative',
                    height: 120,
                    minWidth: `${timelineWidth}px`,
                    display: 'flex',
                    alignItems: 'center',
                    userSelect: 'none'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        height: 6,
                        width: `${progressWidth}px`,
                        backgroundColor: '#1976d2',
                        borderRadius: 3
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        height: 6,
                        width: `${timelineWidth - progressWidth}px`,
                        left: `${progressWidth}px`,
                        backgroundColor: '#b3b3b3',
                        borderRadius: 3
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        backgroundColor: '#2e7d32',
                        border: '2px solid white',
                        boxShadow: '0 0 6px rgba(0,0,0,0.2)'
                    }}
                />

                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        left: -2,
                        top: '20%',
                        color: '#757575'
                    }}
                >
                    {dayjs(startData || start).format('D MMM')}
                </Typography>

                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        top: '65%',
                        color: '#2e7d32',
                        fontWeight: 600
                    }}
                >
                    {t('userPage.timeline.start')}
                </Typography>

                <Box
                    sx={{
                        position: 'absolute',
                        left: `calc(${timelineWidth}px - 11px)`,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        backgroundColor: '#1976d2',
                        border: '2px solid white',
                        boxShadow: '0 0 6px rgba(0,0,0,0.2)'
                    }}
                />

                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        left: `calc(${timelineWidth}px - 14px)`,
                        top: '20%',
                        color: '#757575'
                    }}
                >
                    {dayjs(endData || safeEnd).format('D MMM')}
                </Typography>

                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        left: `calc(${timelineWidth}px - 6px)`,
                        top: '65%',
                        color: '#1976d2',
                        fontWeight: 600
                    }}
                >
                    {t('userPage.timeline.end')}
                </Typography>

                {monthMarkers.map((m, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            left: `${m.position}px`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 15,
                            height: 15,
                            borderRadius: '50%',
                            backgroundColor: '#757575'
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                position: 'absolute',
                                top: '120%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                fontSize: 11,
                                color: '#555'
                            }}
                        >
                            {m.month}
                        </Typography>
                    </Box>
                ))}

                {eventsByDay
                    .filter((e) => visibleTypes[e.type])
                    .map((event, i) => (
                        <Tooltip
                            key={i}
                            title={`${dayjs(event.date).format('DD/MM')}: ${event.events.length} ${event.events.length === 1 ? t('userPage.timeline.session') : t('userPage.timeline.sessions')}`}
                        >
                            <motion.div
                                style={{
                                    position: 'absolute',
                                    left: event.position,
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    if (event.date === selectedDay) {
                                        setSelectedDay(null);
                                        setSelectedEvent(null);
                                    } else {
                                        setSelectedDay(event.date);
                                        setSelectedEvent(null);
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: selectedDay === event.date ? 36 : 28,
                                        height: selectedDay === event.date ? 36 : 28,
                                        borderRadius: '50%',
                                        backgroundColor: getEventColor(event.type),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border:
                                            selectedDay === event.date
                                                ? '3px solid white'
                                                : '2px solid transparent',
                                        boxShadow:
                                            selectedDay === event.date
                                                ? '0 0 10px rgba(0,0,0,0.3)'
                                                : '0 0 4px rgba(0,0,0,0.15)',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {getEventIcon(event.type)}
                                </Box>
                            </motion.div>
                        </Tooltip>
                    ))}

                <Box
                    sx={{
                        position: 'absolute',
                        left: currentPosition,
                        top: '36%',
                        transform: 'translateX(-50%)',
                        width: 2,
                        height: 30,
                        backgroundColor: 'red'
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        left: currentPosition - 12,
                        top: '60%',
                        color: 'red'
                    }}
                >
                    {t('userPage.timeline.today')}
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mt: 2
                }}
            >
                <FormGroup row sx={{ justifyContent: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={visibleTypes.exercise}
                                onChange={() => handleToggleType('exercise')}
                                sx={{
                                    color: getEventColor('exercise'),
                                    '&.Mui-checked': {
                                        color: getEventColor('exercise')
                                    }
                                }}
                            />
                        }
                        label={t('userPage.timeline.exercises')}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={visibleTypes.questionnaire}
                                onChange={() => handleToggleType('questionnaire')}
                                sx={{
                                    color: getEventColor('questionnaire'),
                                    '&.Mui-checked': {
                                        color: getEventColor('questionnaire')
                                    }
                                }}
                            />
                        }
                        label={t('userPage.timeline.questionnaires')}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={visibleTypes.consult}
                                onChange={() => handleToggleType('consult')}
                                sx={{
                                    color: getEventColor('consult'),
                                    '&.Mui-checked': {
                                        color: getEventColor('consult')
                                    }
                                }}
                            />
                        }
                        label={t('userPage.timeline.appointments')}
                    />
                </FormGroup>
            </Box>

            {selectedDay && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    style={{ marginTop: 24 }}
                >
                    <Card
                        elevation={3}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                            mb: 2
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            mb={2}
                            gap={1}
                        >
                            <IconButton
                                onClick={() => {
                                    const allDates = eventsByDay.map((e) => e.date);
                                    const currentIndex = allDates.indexOf(selectedDay);
                                    if (currentIndex < allDates.length - 1) {
                                        const nextDay = allDates[currentIndex + 1];
                                        setSelectedDay(nextDay);
                                        const firstEvent = eventsByDay.find(
                                            (e) => e.date === nextDay
                                        )?.events?.[0];
                                        setSelectedEvent(firstEvent || null);
                                    }
                                }}
                            >
                                <ArrowBackIos color="primary" />
                            </IconButton>

                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                            >
                                {dayjs(selectedDay).format('dddd, D [de] MMMM')} (
                                {
                                    eventsByDay.find((e) => e.date === selectedDay)
                                        ?.events.length
                                }{' '}
                                {eventsByDay.find((e) => e.date === selectedDay)?.events
                                    .length === 1
                                    ? t('userPage.timeline.session')
                                    : t('userPage.timeline.sessions')}
                                )
                            </Typography>

                            <IconButton
                                onClick={() => {
                                    const allDates = eventsByDay.map((e) => e.date);
                                    const currentIndex = allDates.indexOf(selectedDay);
                                    if (currentIndex > 0) {
                                        const prevDay = allDates[currentIndex - 1];
                                        setSelectedDay(prevDay);
                                        const firstEvent = eventsByDay.find(
                                            (e) => e.date === prevDay
                                        )?.events?.[0];
                                        setSelectedEvent(firstEvent || null);
                                    }
                                }}
                            >
                                <ArrowForwardIos color="primary" />
                            </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                            {[
                                ...(eventsByDay.find((e) => e.date === selectedDay)
                                    ?.events || [])
                            ]
                                .sort((a, b) => dayjs(a.ti).diff(dayjs(b.ti)))
                                .map((ev, i) => (
                                    <Grid item xs={12} sm={6} md={4} lg={2.4} key={i}>
                                        <Box
                                            component={motion.div}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setSelectedEvent(ev)}
                                            sx={{
                                                borderRadius: 3,
                                                p: 2,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                border:
                                                    ev.uuid === selectedEvent?.uuid
                                                        ? '2px solid #1976d2'
                                                        : '1px solid #e0e0e0',
                                                backgroundColor:
                                                    ev.uuid === selectedEvent?.uuid
                                                        ? '#e3f2fd'
                                                        : '#F9F9F9',
                                                boxShadow:
                                                    ev.uuid === selectedEvent?.uuid
                                                        ? '0 0 8px rgba(25,118,210,0.4)'
                                                        : '0 1px 3px rgba(0,0,0,0.08)',
                                                '&:hover': {
                                                    backgroundColor: '#eceaea',
                                                    boxShadow:
                                                        '0 2px 8px rgba(0,0,0,0.12)'
                                                }
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                >
                                                    {t('userPage.timeline.session')}
                                                </Typography>

                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                >
                                                    {ev.config?.duration && (
                                                        <Chip
                                                            icon={<FitnessCenter />}
                                                            label={`${Math.round(
                                                                ev.config.duration / 60
                                                            )} min`}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    <Chip
                                                        icon={<EventAvailable />}
                                                        label={dayjs(ev.ti).format(
                                                            'HH:mm'
                                                        )}
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                    />
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                ))}
                        </Grid>
                    </Card>
                </motion.div>
            )}

            {selectedEvent && (
                <EventDetailsCard
                    event={selectedEvent}
                    selected={selected}
                    selectedPlan={selectedPlan}
                    expanded={expanded}
                    onClose={() => setSelectedEvent(null)}
                    onPrev={() => {
                        const dayEvents =
                            eventsByDay.find((e) => e.date === selectedDay)?.events || [];
                        const currentIndex = dayEvents.findIndex(
                            (e) => e.uuid === selectedEvent.uuid
                        );
                        if (currentIndex > 0) {
                            setSelectedEvent(dayEvents[currentIndex - 1]);
                        }
                    }}
                    onNext={() => {
                        const dayEvents =
                            eventsByDay.find((e) => e.date === selectedDay)?.events || [];
                        const currentIndex = dayEvents.findIndex(
                            (e) => e.uuid === selectedEvent.uuid
                        );
                        if (currentIndex < dayEvents.length - 1) {
                            setSelectedEvent(dayEvents[currentIndex + 1]);
                        }
                    }}
                />
            )}
        </Card>
    );
}
